import React, { useEffect, useState } from 'react';
import { Card, Tabs, Table, Statistic, Row, Col, Spin, message, Tag, Button, Space, Popconfirm, Divider, Drawer, Form, Input, Select } from 'antd';
import { FileTextOutlined, ShoppingCartOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import client from '../api/client';
import PesoIcon from '../components/PesoIcon';
import { useAuth } from '../context/AuthContext';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDeactivateCustomer = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditDrawerVisible, setIsEditDrawerVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const customerRes = await client.get(`/customers/${id}`);
      setCustomer(customerRes.data.partner);
      setStats(customerRes.data);
    } catch (error) {
      console.error('Failed to fetch customer details', error.response?.data || error);
      message.error(error.response?.data?.message || 'Failed to fetch customer details');
      navigate('/customers');
      setLoading(false);
      return;
    }

    try {
      const historyRes = await client.get(`/customers/${id}/history`);
      setHistory(historyRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch customer history', error.response?.data || error);
      message.warning(error.response?.data?.message || 'Customer details loaded, but history failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  const handleEditOpen = () => {
    if (customer) {
      const [legacyLastName = '', legacyFirstName = ''] = (customer.name || '').split(/,(.*)/s);
      form.setFieldsValue({
        last_name: customer.last_name || legacyLastName.trim(),
        first_name: customer.first_name || legacyFirstName.trim(),
        address: customer.address,
        phone: customer.phone,
        email: customer.email,
        classification: customer.customerDetail?.classification,
        credit_limit: customer.customerDetail?.credit_limit,
        payment_terms: customer.customerDetail?.payment_terms,
        tin: customer.customerDetail?.tin,
        notes: customer.customerDetail?.notes,
      });
      setIsEditDrawerVisible(true);
    }
  };

  const handleEditSave = async (values) => {
    try {
      await client.put(`/customers/${id}`, values);
      message.success('Customer updated successfully');
      setIsEditDrawerVisible(false);
      fetchCustomerDetails();
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        form.setFields(
          Object.entries(validationErrors).map(([name, errors]) => ({ name, errors }))
        );
      }
      message.error(error.response?.data?.message || 'Failed to update customer');
    }
  };

  const handleDelete = async () => {
    try {
      await client.delete(`/customers/${id}`);
      message.success('Customer deactivated successfully');
      navigate('/customers');
    } catch (error) {
      message.error('Failed to deactivate customer');
    }
  };

  if (loading || !customer) {
    return <Spin spinning={loading} />;
  }

  // Sales Orders Columns
  const salesColumns = [
    {
      title: 'Order #',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 120,
    },
    {
      title: 'Reference #',
      dataIndex: 'reference_number',
      key: 'reference_number',
      width: 120,
      render: (value) => value || 'N/A',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Branch',
      dataIndex: 'branch',
      key: 'branch',
      width: 120,
      render: (val) => val || <span style={{ color: '#bbb' }}>—</span>,
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, record) => {
        const names = (record.items || []).map(i => i.item?.name).filter(Boolean);
        if (!names.length) return <span style={{ color: '#bbb' }}>—</span>;
        return (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {names.map((name, idx) => <li key={idx}>{name}</li>)}
          </ul>
        );
      },
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (amount) => `₱${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: 'Payment Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      render: (status) => {
        const colors = { UNPAID: 'red', PARTIAL: 'orange', PAID: 'green' };
        return <Tag color={colors[status] || 'blue'}>{status}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'CONFIRMED' ? 'blue' : 'default'}>{status}</Tag>
      ),
    },
  ];

  const livestockColumns = [
    {
      title: 'Date',
      dataIndex: 'sale_date',
      key: 'sale_date',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: () => 'Livestock',
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (amount) => `₱${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'COMPLETED' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
  ];

  const salesOrdersData = history.filter((h) => h.type === 'SALES_ORDER');
  const livestockData = history.filter((h) => h.type === 'LIVESTOCK_SALE');

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <ShoppingCartOutlined /> Sales Orders ({salesOrdersData.length})
        </span>
      ),
      children: (
        <Table
          columns={salesColumns}
          dataSource={salesOrdersData}
          rowKey="id"
          pagination={false}
          scroll={{ x: 900 }}
        />
      ),
    },
    {
      key: '2',
      label: (
        <span>
          <FileTextOutlined /> Livestock Purchases ({livestockData.length})
        </span>
      ),
      children: (
        <Table
          columns={livestockColumns}
          dataSource={livestockData}
          rowKey="id"
          pagination={false}
          scroll={{ x: 600 }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/customers')}
        style={{ marginBottom: '16px' }}
      >
        Back to Customers
      </Button>

      {/* Header Card */}
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2>{customer.name}</h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              Code: <strong>{customer.code}</strong>
            </p>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              {customer.address && (
                <>
                  Address: <strong>{customer.address}</strong>
                  <br />
                </>
              )}
              {customer.phone && (
                <>
                  Phone: <strong>{customer.phone}</strong>
                  <br />
                </>
              )}
              {customer.email && (
                <>
                  Email: <strong>{customer.email}</strong>
                  <br />
                </>
              )}
            </p>
            {customer.customerDetail?.classification && (
              <p style={{ color: '#666', marginBottom: '0' }}>
                Classification: <Tag>{customer.customerDetail.classification}</Tag>
              </p>
            )}
          </div>
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEditOpen}
            >
              Edit
            </Button>
            {canDeactivateCustomer && (
              <Popconfirm
                title="Deactivate Customer"
                description="Are you sure you want to deactivate this customer?"
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />}>
                  Deactivate
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      </Card>

      {/* Summary Stats */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Purchases"
              value={stats?.total_purchases || 0}
              prefix={<PesoIcon />}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Paid"
              value={stats?.total_paid || 0}
              prefix={<PesoIcon />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Outstanding Balance"
              value={stats?.outstanding_balance || 0}
              prefix={<PesoIcon />}
              precision={2}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Number of Orders"
              value={stats?.order_count || 0}
            />
          </Card>
        </Col>
      </Row>

      {/* Purchase History Tabs */}
      <Card>
        <h3>Purchase History</h3>
        <Divider />
        <Tabs items={tabItems} />
      </Card>

      {/* Edit Drawer */}
      <Drawer
        title="Edit Customer"
        placement="right"
        onClose={() => setIsEditDrawerVisible(false)}
        open={isEditDrawerVisible}
        width={400}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSave}
        >
          <Form.Item
            label="Last Name"
            name="last_name"
            rules={[
              { required: true, whitespace: true, message: 'Please enter the last name' },
              { max: 120, message: 'Last name must not exceed 120 characters' },
            ]}
          >
            <Input placeholder="e.g., Dela Cruz" />
          </Form.Item>

          <Form.Item
            label="First Name"
            name="first_name"
            rules={[
              { required: true, whitespace: true, message: 'Please enter the first name' },
              { max: 120, message: 'First name must not exceed 120 characters' },
            ]}
          >
            <Input placeholder="e.g., Juan" />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phone"
          >
            <Input placeholder="Phone number" />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Email address" />
          </Form.Item>

          <Form.Item
            label="Address"
            name="address"
          >
            <Input.TextArea placeholder="Address" rows={2} />
          </Form.Item>

          <Form.Item
            label="Classification"
            name="classification"
          >
            <Select
              placeholder="Select classification"
              options={[
                { label: 'Wholesaler', value: 'WHOLESALER' },
                { label: 'Retailer', value: 'RETAILER' },
                { label: 'Regular', value: 'REGULAR' },
                { label: 'Walk-in', value: 'WALKIN' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Credit Limit"
            name="credit_limit"
            type="number"
          >
            <Input type="number" placeholder="0" />
          </Form.Item>

          <Form.Item
            label="Payment Terms"
            name="payment_terms"
          >
            <Select
              placeholder="Select payment terms"
              options={[
                { label: 'Cash on Delivery', value: 'COD' },
                { label: 'Net 30', value: 'NET30' },
                { label: 'Net 60', value: 'NET60' },
                { label: 'Net 90', value: 'NET90' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="TIN"
            name="tin"
          >
            <Input placeholder="Tax Identification Number" />
          </Form.Item>

          <Form.Item
            label="Notes"
            name="notes"
          >
            <Input.TextArea placeholder="Additional notes" rows={2} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Save Changes
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
